import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { API } from '@/utils/API';
import { Clarification } from '@/types/clarification';
import { useDebugRole } from '@/contexts/DebugRoleContext';
import { isClarificationsActiveStatus } from '@/utils/clarificationStatus';

interface BatchClarificationsData {
  /** All clarifications grouped by question slug */
  clarificationsByQuestion: Record<string, Clarification[]>;
  /** Permission flags (returned once from batch endpoint) */
  canAddClarifications: boolean;
  canResolveClarifications: boolean;
  canEditClarifications: boolean;
  canReplyOnly: boolean;
  /** Whether persisted ops clarification activity exists since the last customer response */
  hasMaterialToRequestClarifications: boolean;
  /** Whether persisted customer replies / answer edits exist since the last clarification request */
  hasMaterialToSubmitClarificationResponses: boolean;
  submissionStatus: string | null;
  /** General workflow overlay (sponsor/CRO, translation, hold, etc.) — no
   *  longer carries clarification state; see clarificationRoundState. */
  subStatus: string | null;
  /** The submission's ClarificationRound state: 'drafting' | 'requested' |
   *  'responded' | 'resolved' | null (no round yet). This is the single
   *  source of truth for clarification visibility/editability — replaces
   *  the old sub_status === 'pending_clarification'/'clarification_received'
   *  checks, which no longer occur (see
   *  .claude/plans/clarification-lifecycle-rework.md). */
  clarificationRoundState: string | null;
}

interface ClarificationsContextValue extends BatchClarificationsData {
  isLoading: boolean;
  error: string | null;
  /** Get clarifications for a specific question */
  getClarificationsForQuestion: (questionSlug: string) => Clarification[];
  /** Get count for a specific question */
  getCountForQuestion: (questionSlug: string) => number;
  /** Get unresolved count for a specific question */
  getUnresolvedCountForQuestion: (questionSlug: string) => number;
  /** Create a clarification (still per-question, but refetches batch) */
  createClarification: (
    questionSlug: string,
    content: string,
    parentId?: string | null
  ) => Promise<void>;
  /** Resolve a clarification */
  resolveClarification: (
    questionSlug: string,
    clarificationId: string
  ) => Promise<void>;
  /** Unresolve a clarification */
  unresolveClarification: (
    questionSlug: string,
    clarificationId: string
  ) => Promise<void>;
  /** Update the content of a clarification message (author only).
   *  Pass messageId to edit a reply; omit to edit the parent message. */
  updateClarification: (
    clarificationId: string,
    content: string,
    messageId?: string
  ) => Promise<void>;
  /** Edit the answer value for a question.
   *  With a clarification thread on the field: PATCH edit_answer (audit on thread).
   *  With no thread: POST submission_answers only while the clarification
   *  round is +requested+ (same policy as SubmissionPolicy#edit?). */
  editAnswer: (questionSlug: string, answerValue: string) => Promise<void>;
  /** Whether all root comment clarifications across the submission are resolved */
  allClarificationsResolved: boolean;
  /** Total number of root comment clarifications across the submission */
  totalClarificationCount: number;
  /** Refetch all clarifications */
  refetch: () => Promise<void>;
}

const defaultValue: ClarificationsContextValue = {
  clarificationsByQuestion: {},
  canAddClarifications: false,
  canResolveClarifications: false,
  canEditClarifications: false,
  canReplyOnly: false,
  hasMaterialToRequestClarifications: false,
  hasMaterialToSubmitClarificationResponses: false,
  submissionStatus: null,
  subStatus: null,
  clarificationRoundState: null,
  isLoading: false,
  error: null,
  getClarificationsForQuestion: () => [],
  getCountForQuestion: () => 0,
  getUnresolvedCountForQuestion: () => 0,
  createClarification: async () => {},
  resolveClarification: async () => {},
  unresolveClarification: async () => {},
  updateClarification: async () => {},
  editAnswer: async () => {},
  allClarificationsResolved: true,
  totalClarificationCount: 0,
  refetch: async () => {},
};

const ClarificationsContext =
  createContext<ClarificationsContextValue>(defaultValue);

interface ClarificationsProviderProps {
  children: React.ReactNode;
  submissionId: string | undefined;
  /** Submission status - used to skip fetching for drafts */
  submissionStatus?: string | null;
}

export const ClarificationsProvider: React.FC<ClarificationsProviderProps> = ({
  children,
  submissionId,
  submissionStatus,
}) => {
  const [data, setData] = useState<BatchClarificationsData>({
    clarificationsByQuestion: {},
    canAddClarifications: false,
    canResolveClarifications: false,
    canEditClarifications: false,
    canReplyOnly: false,
    hasMaterialToRequestClarifications: false,
    hasMaterialToSubmitClarificationResponses: false,
    submissionStatus: null,
    subStatus: null,
    clarificationRoundState: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Access debug role and feature flag for sending header with API calls
  const { debugRole, clarificationsEnabled } = useDebugRole();

  // Build debug headers for development-only role simulation
  const getDebugHeaders = useCallback((): Record<string, string> => {
    if (debugRole === 'real') return {};
    return { 'X-Debug-Role': debugRole };
  }, [debugRole]);

  const fetchBatchClarifications = useCallback(async () => {
    if (
      !submissionId ||
      !submissionStatus ||
      !clarificationsEnabled ||
      !isClarificationsActiveStatus(submissionStatus)
    ) {
      setData({
        clarificationsByQuestion: {},
        canAddClarifications: false,
        canResolveClarifications: false,
        canEditClarifications: false,
        canReplyOnly: false,
        hasMaterialToRequestClarifications: false,
        hasMaterialToSubmitClarificationResponses: false,
        submissionStatus: submissionStatus || null,
        subStatus: null,
        clarificationRoundState: null,
      });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await API.authenticated.fetch(
        `/submissions/${submissionId}/clarifications/batch`,
        { headers: getDebugHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch clarifications');
      }

      const json = await response.json();
      setData({
        clarificationsByQuestion: json.clarifications_by_question || {},
        canAddClarifications: json.can_add_clarifications ?? false,
        canResolveClarifications: json.can_resolve_clarifications ?? false,
        canEditClarifications: json.can_edit_clarifications ?? false,
        canReplyOnly: json.can_reply_only ?? false,
        hasMaterialToRequestClarifications:
          json.has_material_to_request_clarifications ?? false,
        hasMaterialToSubmitClarificationResponses:
          json.has_material_to_submit_clarification_responses ?? false,
        submissionStatus: json.submission_status || null,
        subStatus: json.sub_status || null,
        clarificationRoundState: json.clarification_round?.state ?? null,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch clarifications'
      );
    } finally {
      setIsLoading(false);
    }
  }, [submissionId, submissionStatus, clarificationsEnabled, getDebugHeaders]);

  // Re-fetch whenever any input changes. fetchBatchClarifications is memoized
  // via useCallback on [submissionId, submissionStatus, clarificationsEnabled,
  // getDebugHeaders], so the effect only fires when something meaningful changes.
  // The callback itself handles early-return + data reset for draft/disabled states.
  useEffect(() => {
    fetchBatchClarifications();
  }, [fetchBatchClarifications]);

  const getClarificationsForQuestion = useCallback(
    (questionSlug: string): Clarification[] => {
      return data.clarificationsByQuestion[questionSlug] || [];
    },
    [data.clarificationsByQuestion]
  );

  const getCountForQuestion = useCallback(
    (questionSlug: string): number => {
      const clarifications = data.clarificationsByQuestion[questionSlug] || [];
      return clarifications.reduce(
        (total, c) => total + 1 + (c.replies?.length || 0),
        0
      );
    },
    [data.clarificationsByQuestion]
  );

  const getUnresolvedCountForQuestion = useCallback(
    (questionSlug: string): number => {
      const clarifications = data.clarificationsByQuestion[questionSlug] || [];
      return clarifications.filter(c => !c.resolved).length;
    },
    [data.clarificationsByQuestion]
  );

  const createClarification = useCallback(
    async (questionSlug: string, content: string, parentId?: string | null) => {
      if (!submissionId) return;

      try {
        // If parentId is provided, add a message to existing clarification
        // Otherwise, create a new clarification thread
        const url = parentId
          ? `/submissions/${submissionId}/clarifications/${parentId}/messages`
          : `/submissions/${submissionId}/clarifications?question_slug=${encodeURIComponent(questionSlug)}`;

        const method = 'POST';

        const response = await API.authenticated.fetch(url, {
          method,
          headers: getDebugHeaders(),
          body: JSON.stringify({ content }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to create clarification');
        }

        // Refetch batch data after creating
        await fetchBatchClarifications();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to create clarification'
        );
        throw err;
      }
    },
    [submissionId, fetchBatchClarifications, getDebugHeaders]
  );

  const resolveClarification = useCallback(
    async (questionSlug: string, clarificationId: string) => {
      if (!submissionId) return;

      try {
        const response = await API.authenticated.fetch(
          `/submissions/${submissionId}/clarifications/${clarificationId}/resolve`,
          { method: 'PATCH', headers: getDebugHeaders() }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to resolve clarification');
        }

        // Refetch batch data after resolving
        await fetchBatchClarifications();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to resolve clarification'
        );
        throw err;
      }
    },
    [submissionId, fetchBatchClarifications, getDebugHeaders]
  );

  const unresolveClarification = useCallback(
    async (questionSlug: string, clarificationId: string) => {
      if (!submissionId) return;

      try {
        const response = await API.authenticated.fetch(
          `/submissions/${submissionId}/clarifications/${clarificationId}/unresolve`,
          { method: 'PATCH', headers: getDebugHeaders() }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || 'Failed to unresolve clarification'
          );
        }

        // Refetch batch data after unresolving
        await fetchBatchClarifications();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Failed to unresolve clarification'
        );
        throw err;
      }
    },
    [submissionId, fetchBatchClarifications, getDebugHeaders]
  );

  const updateClarification = useCallback(
    async (clarificationId: string, content: string, messageId?: string) => {
      if (!submissionId) return;

      try {
        const body: Record<string, string> = { content };
        if (messageId) body.message_id = messageId;

        const response = await API.authenticated.fetch(
          `/submissions/${submissionId}/clarifications/${clarificationId}/edit_content`,
          {
            method: 'PATCH',
            headers: getDebugHeaders(),
            body: JSON.stringify(body),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to update clarification');
        }

        await fetchBatchClarifications();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to update clarification'
        );
        throw err;
      }
    },
    [submissionId, fetchBatchClarifications, getDebugHeaders]
  );

  const editAnswer = useCallback(
    async (questionSlug: string, answerValue: string) => {
      if (!submissionId) return;

      const threadsForQuestion =
        data.clarificationsByQuestion[questionSlug] || [];
      const saveViaSubmissionAnswers =
        threadsForQuestion.length === 0 &&
        data.clarificationRoundState === 'requested';

      try {
        const headers = getDebugHeaders();
        const response = saveViaSubmissionAnswers
          ? await API.authenticated.fetch(
              `/submissions/${submissionId}/submission_answers`,
              {
                method: 'POST',
                headers,
                body: JSON.stringify({
                  answers: [
                    {
                      question_slug: questionSlug,
                      answer_value: answerValue,
                    },
                  ],
                }),
              }
            )
          : await API.authenticated.fetch(
              `/submissions/${submissionId}/clarifications/edit_answer?question_slug=${encodeURIComponent(questionSlug)}`,
              {
                method: 'PATCH',
                headers,
                body: JSON.stringify({ answer_value: answerValue }),
              }
            );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to edit answer');
        }

        await fetchBatchClarifications();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to edit answer');
        throw err;
      }
    },
    [
      submissionId,
      fetchBatchClarifications,
      getDebugHeaders,
      data.clarificationsByQuestion,
      data.clarificationRoundState,
    ]
  );

  // Compute aggregate clarification stats across all questions
  const { allClarificationsResolved, totalClarificationCount } = useMemo(() => {
    const allClarifications = Object.values(
      data.clarificationsByQuestion
    ).flat();
    const unresolvedClarifications = allClarifications.filter(c => !c.resolved);
    return {
      allClarificationsResolved: unresolvedClarifications.length === 0,
      totalClarificationCount: allClarifications.length,
    };
  }, [data.clarificationsByQuestion]);

  // Permission flags (canAddClarifications, canResolveClarifications, canReplyOnly)
  // come directly from the backend batch response. The backend already handles
  // debug-role simulation via the X-Debug-Role header sent by getDebugHeaders(),
  // so no frontend override is needed — the re-fetch triggered by a debug-role
  // switch returns the correct flags.
  const value = useMemo<ClarificationsContextValue>(
    () => ({
      ...data,
      isLoading,
      error,
      getClarificationsForQuestion,
      getCountForQuestion,
      getUnresolvedCountForQuestion,
      createClarification,
      resolveClarification,
      unresolveClarification,
      updateClarification,
      editAnswer,
      allClarificationsResolved,
      totalClarificationCount,
      refetch: fetchBatchClarifications,
    }),
    [
      data,
      isLoading,
      error,
      getClarificationsForQuestion,
      getCountForQuestion,
      getUnresolvedCountForQuestion,
      createClarification,
      resolveClarification,
      unresolveClarification,
      updateClarification,
      editAnswer,
      allClarificationsResolved,
      totalClarificationCount,
      fetchBatchClarifications,
    ]
  );

  return (
    <ClarificationsContext.Provider value={value}>
      {children}
    </ClarificationsContext.Provider>
  );
};

export const useClarificationsContext = (): ClarificationsContextValue => {
  return useContext(ClarificationsContext);
};
